// Implements: F-UIX-017
/**
 * A keyboard shortcut of the page (F-UIX-017): Escape cancels the turn in flight,
 * Alt+Shift+Arrow changes version.
 *
 * Every shortcut follows the same rules, so none of them surprises the person:
 * - it fires only while `enabled` (there is something to do), and does exactly what its
 *   button does, through the same `onPress`;
 * - it never fires from a field: a key pressed while typing belongs to the field;
 * - its modifiers are exact, and Ctrl or Cmd are never part of one: those combinations
 *   belong to the browser and the system;
 * - an event somebody already handled (`defaultPrevented`) is left alone, and a held key
 *   (auto-repeat) presses it once, not once per repeat;
 * - while something is open over the page (a dialog, a maximised preview) it never fires, from
 *   wherever the focus is: that Escape is the overlay's to close it. Clicking a part of a dialog
 *   that takes no focus leaves the focus on the page, and an overlay that traps no focus lets Tab
 *   walk back into the conversation; neither turns that Escape into a cancel;
 * - bound to a `region`, it fires only from inside it or from the page with nothing focused,
 *   so an Escape meant for a menu or a dialog elsewhere stays theirs;
 * - the listener goes away with the component (no leak across pages or entities).
 */

import { useEffect } from "react";
import type { RefObject } from "react";

import { isEditableTarget } from "./editableTarget";

export interface KeyShortcut {
  /** `KeyboardEvent.key`, e.g. `Escape` or `ArrowRight`. */
  readonly key: string;
  readonly alt?: boolean | undefined;
  readonly shift?: boolean | undefined;
}

/** The shortcut as `aria-keyshortcuts` spells it: modifiers first, then the key. */
export function keyShortcutLabel(shortcut: KeyShortcut): string {
  return [shortcut.alt === true ? "Alt" : null, shortcut.shift === true ? "Shift" : null, shortcut.key]
    .filter((part) => part !== null)
    .join("+");
}

/** What is open over the page: a dialog (modal or not) or the maximised preview. */
const OVERLAY = ['[role="dialog"]', '[aria-modal="true"]', "dialog[open]", "[data-maximised]"].join(", ");

/**
 * Something is open over the page, the browser's own full screen included (the preview's
 * `requestFullscreen`): the Escape that leaves it never cancels a paid turn, even when the
 * browser also delivers that keydown to the page. Engines without the API leave it undefined.
 */
function overlayOpen(): boolean {
  return document.querySelector(OVERLAY) !== null || (document.fullscreenElement ?? null) !== null;
}

function fromPage(target: EventTarget | null): boolean {
  return target === null || target === document || target === document.body || target === document.documentElement;
}

export function useKeyShortcut(
  shortcut: KeyShortcut,
  enabled: boolean,
  onPress: () => void,
  region?: RefObject<HTMLElement | null>,
): void {
  const { key } = shortcut;
  const alt = shortcut.alt === true;
  const shift = shortcut.shift === true;

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== key || event.defaultPrevented || event.repeat) return;
      if (event.ctrlKey || event.metaKey || event.altKey !== alt || event.shiftKey !== shift) return;
      const { target } = event;
      if (isEditableTarget(target) || overlayOpen()) return;
      if (region !== undefined && !fromPage(target)) {
        if (!(target instanceof Node) || region.current?.contains(target) !== true) return;
      }
      event.preventDefault();
      onPress();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [key, alt, shift, enabled, onPress, region]);
}
